import {
  Client,
  ClientDetail,
  Delegation,
  IAssistanceService,
  IChannelData,
  IClient,
  IClientDetailData,
  IClientParams,
  IDelegation,
  IDeliveryService,
  IPickUpService,
  IReport,
  IServicepoint,
  IServicepointDetail,
  IServicepointParams,
  ISolCustomerServiceRating,
  ISolCustomerSupplyMaterialService,
  ISolResponse,
  ISubclient,
  ISubclientDetailData,
  ISubclientParams,
  IUpdateClient,
  IUpdateServicepoint,
  IUpdateSubclient,
  IUpdateUser,
  IUpdateUserOID,
  IUser,
  IUserClientReportTypeData,
  IUserDetail,
  Report,
  Servicepoint,
  ServicepointDetail,
  Subclient,
  SubclientDetail,
  User,
  UserDetail,
  CountryConfig,
} from "@/entities";
import {
  defineRulesFor,
  IGenericResponse,
  II18n,
  IPageRequest,
  IPageResponse,
  IRequestHistorialParams,
  IRequestHistorialResponse,
  ISolCustomerCurrencyParams,
  ISolCustomerDenominationParams,
  ISolCustomerDenominationResponse,
  ISolCustomerEmployeeParams,
  ISolCustomerEmployeeResponse,
  ISolCustomerFaultParams,
  ISolCustomerFaultResponse,
  ISolCustomerGetAssistanceServiceResponse,
  ISolCustomerGetDeliveryServiceResponse,
  ISolCustomerGetPickUpServiceResponse,
  ISolCustomerGetServiceParams,
  ISolCustomerGetSupplyMaterialServiceResponse,
  ISolCustomerImageParams,
  ISolCustomerImageResponse,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
  ISolCustomerResponse,
  ISolCustomerServiceRatingResponse,
  ISolCustomerSupplyParams,
  ISolCustomerSupplyResponse,
  ISolCustomerVehicleParams,
  ISolCustomerVehicleResponse,
  IUserRequest,
  IValidateSolClientParams,
  IValidateSolClientResponse,
  IValidateSolServicepointParams,
  IValidateSolServicepointResponse,
  IValidateSolSubclientParams,
  IValidateSolSubclientResponse,
  ISolCustomerOrderCancellationRequestBody,
  ISolCustomerCancellationReasonResponse,
  IUsersReportRequest,
  IReportRequest,
  IRequestClientsReport,
  IEmployeeSearchReportRequest,
  ISubclientsReportRequest,
  IServicePointReportRequest,
  IClientsReportRequest,
} from "@/services";
import { Locale, Context, IActions } from "@/store";
import { AxiosResponse } from "axios";

export const actions: IActions = {
  async initialize(context: Context): Promise<void> {
    const isLoggedIn = await context.dispatch("fetchSignin");

    if (isLoggedIn) {
      await context.dispatch("fetchLoggedUser").finally(async () => {
        const user: IUser | undefined = context.state.loggedUser;

        await Promise.all([
          context.dispatch("fetchCountriesPage"),
          context.dispatch("fetchDisclaimer"),
          context.dispatch("fetchUsersGuide"),
          context.dispatch("fetchCurrenciesPage", {
            page: 0,
            size: 1000,
            sort: "code,ASC",
          } as IPageRequest),
          context.dispatch("fetchAssignableAdapters"),
          context.dispatch("fetchCountryConfig", user?.country?.code),
          context.dispatch("fetchCashCountryConfig", user?.country?.code),
        ]).finally(async () => {
          // Evaluamos permisos a partir de las reglas
          const config = context.state.countryConfig
            ? new CountryConfig(context.state.countryConfig)
            : undefined;
          const rules = defineRulesFor(context.state.loggedUser, config);
          const canReadOrdersPage = rules.some(
            (r) => r.action === "read" && (r.subject as any) === "OrdersPage"
          );
          const canReadApprovalsPage = rules.some(
            (r) => r.action === "read" && (r.subject as any) === "ApprovalsPage"
          );

          if (canReadOrdersPage || canReadApprovalsPage) {
            await context.dispatch("fetchSolCustomerCurrency", {
              countryCode: user?.country?.code,
            } as ISolCustomerCurrencyParams);
          }

          context.commit("SET_SIGNED_IN", true);
        });
      });
    }
  },

  setCurrentLocation(context: Context, location: string) {
    context.commit("SET_CURRENT_LOCATION", location);
  },

  setCurrentLocale(context: Context, locale: Locale) {
    context.commit("SET_CURRENT_LOCALE", locale);
  },

  async fetchLoggedUser(context: Context): Promise<void> {
    const res = await this.$services.user.getLoggedUser();
    context.commit("SET_LOGGED_USER", res);
  },

  async fetchDisclaimer(context: Context): Promise<void> {
    const res = await this.$services.disclaimer.getDisclaimerFile();
    context.commit("SET_DISCLAIMER", res);
  },

  async fetchUsersGuide(context: Context): Promise<void> {
    const res = await this.$services.usersGuide.getUsersGuide();
    context.commit("SET_USERS_GUIDE", res);
  },

  async fetchCountryConfig(context: Context, code: string): Promise<void> {
    const res = await this.$services.country.getConfig(code);
    context.commit("SET_COUNTRY_CONFIG", res);
  },

  async fetchCashCountryConfig(context: Context, code: string): Promise<void> {
    const res = await this.$services.country.getCountryConfigByCode(code);
    context.commit("SET_CASH_COUNTRY_CONFIG", res);
  },

  async fetchI18n(): Promise<II18n | undefined> {
    return this.$services.i18n.getI18n();
  },

  async fetchUsersPage(
    context: Context,
    params?: IUserRequest
  ): Promise<IPageResponse<IUser> | undefined> {
    const res = await this.$services.user.getUsersPage(params);

    res?.content?.map((data) => new User(data));

    return res;
  },

  async fetchReportsPage(
    context: Context,
    params?: IReportRequest
  ): Promise<IPageResponse<IReport> | undefined> {
    const res = await this.$services.report.getReportsPage(params);

    res?.content.map((data) => new Report(data));
    return res;
  },

  async fetchDeleteReport(content: Context, id: number): Promise<void> {
    await this.$services.report.deleteReport(id);
  },

  async fetchDownloadReport(
    content: Context,
    id: number
  ): Promise<AxiosResponse<Blob> | undefined> {
    return await this.$services.report.downloadReport(id);
  },

  async storeUsersReport(context: Context, params?: IUsersReportRequest): Promise<void> {
    await this.$services.user.saveUsersReport(params);
  },

  async fetchUserDetail(context: Context, idUser: number): Promise<IUserDetail | undefined> {
    const res = await this.$services.user.getUserDetail(idUser);

    return res ? new UserDetail(res) : undefined;
  },

  async updateOIDSol(
    context: Context,
    payload: { idUser: number | undefined; updateUser: IUpdateUserOID }
  ): Promise<string | undefined> {
    return await this.$services.user.updateOIDSol(payload.idUser, payload.updateUser);
  },

  async updateUser(
    context: Context,
    payload: { idUser: number | undefined; updateUser: IUpdateUser | undefined }
  ): Promise<ISolResponse | undefined> {
    return this.$services.user.updateUser(payload.idUser, payload.updateUser);
  },

  async addUserServicepointApproval(
    context: Context,
    payload: { idUser: number; updateUser: IUpdateUser }
  ): Promise<boolean | undefined> {
    return this.$services.user.addUserServicepointApproval(payload.idUser, payload.updateUser);
  },

  async removeUserServicepoint(
    context: Context,
    payload: { idUser: number; updateUser: IUpdateUser }
  ): Promise<ISolResponse | undefined> {
    return this.$services.user.removeUserServicepoint(payload.idUser, payload.updateUser);
  },

  async removeUserServicepointApprovals(
    context: Context,
    { idUser, updateUser }: { idUser: number; updateUser: IUpdateUser }
  ): Promise<boolean | undefined> {
    return this.$services.user.removeUserServicepointApprovals(idUser, updateUser);
  },

  async toggleAdmin(context: Context, idUser: number): Promise<boolean | undefined> {
    return this.$services.user.toggleAdmin(idUser);
  },

  async toggleSupervisor(context: Context, idUser: number): Promise<boolean | undefined> {
    return this.$services.user.toggleSupervisor(idUser);
  },

  async toggleAprobador(context: Context, idUser: number): Promise<boolean | undefined> {
    return this.$services.user.toggleAprobador(idUser);
  },

  async fetchClientsPage(
    context: Context,
    params: IClientParams
  ): Promise<IPageResponse<IClient> | undefined> {
    const res = await this.$services.client.getClients(params);

    res?.content?.map((data) => new Client(data));

    return res;
  },

  async fetchClientDetail(
    context: Context,
    idUser: number
  ): Promise<IClientDetailData | undefined> {
    const client: IClientDetailData | undefined = await this.$services.client.getClientDetail(
      idUser
    );

    if (!client) {
      return;
    }

    return new ClientDetail(client);
  },

  async saveUpdateClient(
    context: Context,
    updateClient: IUpdateClient
  ): Promise<IGenericResponse<IClientDetailData> | undefined> {
    return this.$services.client.saveUpdateClient(updateClient);
  },

  async updateStatusClient(context: Context, idClient: number): Promise<void> {
    await this.$services.client.updateStatusClient(idClient);
  },

  async fetchSubclientsPage(
    context: Context,
    params: ISubclientParams
  ): Promise<IPageResponse<ISubclient> | undefined> {
    const res = await this.$services.subclient.getSubclients(params);

    res?.content?.map((data) => new Subclient(data));

    return res;
  },

  async fetchSubclientDetail(
    context: Context,
    idSubclient: number
  ): Promise<ISubclientDetailData | undefined> {
    const res = await this.$services.subclient.getSubclientDetail(idSubclient);

    if (!res) {
      return;
    }

    return new SubclientDetail(res);
  },

  async saveUpdateSubclient(
    context: Context,
    updateSubclient: IUpdateSubclient
  ): Promise<IGenericResponse<ISubclientDetailData> | undefined> {
    return this.$services.subclient.saveUpdateSubclient(updateSubclient);
  },

  async updateStatusSubclient(context: Context, idSubclient: number): Promise<void> {
    await this.$services.subclient.updateStatusSubclient(idSubclient);
  },

  async fetchServicepointsPage(
    context: Context,
    params: IServicepointParams
  ): Promise<IPageResponse<IServicepoint> | undefined> {
    const res = await this.$services.servicepoint.getServicepoints(params);

    res?.content?.map((data) => new Servicepoint(data));

    return res;
  },

  async fetchServicepointDetail(
    context: Context,
    idServicepoint: number
  ): Promise<IServicepointDetail | undefined> {
    const res = await this.$services.servicepoint.getServicepoint(idServicepoint);

    if (!res) {
      return;
    }

    return new ServicepointDetail(res);
  },

  async saveUpdateServicepoint(
    context: Context,
    updateServicepoint: IUpdateServicepoint
  ): Promise<IGenericResponse<IServicepointDetail> | undefined> {
    return this.$services.servicepoint.saveUpdateServicepoint(updateServicepoint);
  },

  async updateStatusServicepoint(context: Context, idServicepoint: number): Promise<void> {
    await this.$services.servicepoint.updateStatusServicepoint(idServicepoint);
  },

  async fetchCountriesPage(context: Context, sort?: IPageRequest): Promise<void> {
    const res = await this.$services.country.getCountries(sort);

    context.commit("SET_COUNTRIES_PAGE", res);
  },

  async fetchDelegations(): Promise<IDelegation[] | undefined> {
    const res = await this.$services.delegation.getDelegations();

    return res?.map((data) => new Delegation(data));
  },

  async fetchDelegationsByCountry(
    context: Context,
    idCountry: number | undefined
  ): Promise<IDelegation[] | undefined> {
    const res = await this.$services.delegation.getDelegationsByCountry(idCountry);

    return res?.map((data) => new Delegation(data));
  },

  async fetchAssignableAdapters(context: Context): Promise<void> {
    const res = await this.$services.adapter.getAssignableAdapters();
    context.commit("SET_ASSIGNABLE_ADAPTERS", res);
  },

  async fetchCurrenciesPage(context: Context, params: IPageRequest): Promise<void> {
    const res = await this.$services.currency.getCurrenciesPage(params);

    context.commit("SET_CURRENCIES_PAGE", res);
  },

  async fetchChannelsPage(
    context: Context,
    {
      pageRequest,
      text,
      countryId,
    }: { pageRequest: IPageRequest; text?: string; countryId: number }
  ): Promise<IPageResponse<IChannelData> | undefined> {
    return this.$services.channel.getChannelsPage(pageRequest, text, countryId);
  },

  async createChannel(
    context: Context,
    { text, countryId }: { text: string; countryId: number }
  ): Promise<IGenericResponse<IChannelData> | undefined> {
    return this.$services.channel.createChannel(text, countryId);
  },

  async validateSolClient(
    context: Context,
    payload: { countryCode: string; params: IValidateSolClientParams }
  ): Promise<IValidateSolClientResponse | undefined> {
    return this.$services.sol.validateSolClient(payload.countryCode, payload.params);
  },

  async validateSolSubclient(
    context: Context,
    payload: {
      countryCode: string;
      params: IValidateSolSubclientParams;
    }
  ): Promise<IValidateSolSubclientResponse | undefined> {
    return this.$services.sol.validateSolSubclient(payload.countryCode, payload.params);
  },

  async validateSolServicepoint(
    context: Context,
    payload: {
      countryCode: string;
      params: IValidateSolServicepointParams;
    }
  ): Promise<IValidateSolServicepointResponse | undefined> {
    return this.$services.sol.validateSolServicepoint(payload.countryCode, payload.params);
  },

  async fetchEmployee(
    context: Context,
    params: ISolCustomerEmployeeParams
  ): Promise<ISolCustomerEmployeeResponse | undefined> {
    return this.$services.solCustomer.getEmployee(params);
  },

  async fetchVehicle(
    context: Context,
    params: ISolCustomerVehicleParams
  ): Promise<ISolCustomerVehicleResponse | undefined> {
    return this.$services.solCustomer.getVehicle(params);
  },

  async fetchImage(
    context: Context,
    params: ISolCustomerImageParams
  ): Promise<ISolCustomerImageResponse | undefined> {
    return this.$services.solCustomer.getImage(params);
  },

  async fetchRequestHistorial(
    context: Context,
    params: IRequestHistorialParams
  ): Promise<IRequestHistorialResponse | undefined> {
    return this.$services.solCustomer.orders?.getRequestHistorial(params);
  },

  async fetchSolCustomerCurrency(
    context: Context,
    params: ISolCustomerCurrencyParams
  ): Promise<void> {
    const res = await this.$services.solCustomer.currency?.getCurrency(params);
    context.commit("SET_SOLCUSTOMER_CURRENCIES", res?.currencies);
  },

  async savePickUpService(
    context: Context,
    pickUpService: IPickUpService
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.savePickUpService(pickUpService);
  },

  async fetchSolCustomerFaults(
    context: Context,
    params: ISolCustomerFaultParams
  ): Promise<ISolCustomerFaultResponse | undefined> {
    return this.$services.solCustomer.orders?.getFaults(params);
  },

  async fetchSolCustomerDenominations(
    context: Context,
    params: ISolCustomerDenominationParams
  ): Promise<ISolCustomerDenominationResponse | undefined> {
    return this.$services.solCustomer.orders?.getDenominations(params);
  },

  async fetchSolCustomerPacks(
    context: Context,
    params: ISolCustomerPackParams
  ): Promise<ISolCustomerPackResponse | undefined> {
    return this.$services.solCustomer.orders?.getPacks(params);
  },

  async saveAssistanceService(
    context: Context,
    assistanceService: IAssistanceService
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.saveAssistanceService(assistanceService);
  },

  async saveDeliveryService(
    context: Context,
    deliveryService: IDeliveryService
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.saveDeliveryService(deliveryService);
  },

  async fetchSolCustomerSupply(
    context: Context,
    params: ISolCustomerSupplyParams
  ): Promise<ISolCustomerSupplyResponse | undefined> {
    return this.$services.solCustomer.orders?.getSupply(params);
  },

  async saveSupplyMaterialService(
    context: Context,
    supplyMaterialService: ISolCustomerSupplyMaterialService
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.saveSupplyMaterialService(supplyMaterialService);
  },

  async saveServiceRating(
    context: Context,
    serviceRating: ISolCustomerServiceRating
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.saveServiceRating(serviceRating);
  },

  async fetchServiceRating(
    context: Context,
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerServiceRatingResponse | undefined> {
    return this.$services.solCustomer.orders?.getServiceRating(params);
  },

  async fetchPickUpService(
    context: Context,
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetPickUpServiceResponse | undefined> {
    return this.$services.solCustomer.orders?.getPickUpService(params);
  },

  async fetchAssistanceService(
    context: Context,
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetAssistanceServiceResponse | undefined> {
    return this.$services.solCustomer.orders?.getAssistanceService(params);
  },

  async fetchDeliveryService(
    context: Context,
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetDeliveryServiceResponse | undefined> {
    return this.$services.solCustomer.orders?.getDeliveryService(params);
  },

  async fetchSupplyMaterialService(
    context: Context,
    params: ISolCustomerGetServiceParams
  ): Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined> {
    return this.$services.solCustomer.orders?.getSupplyMaterialService(params);
  },

  async fetchCancellationReasonsService(
    context: Context,
    countryCode: string
  ): Promise<ISolCustomerCancellationReasonResponse | undefined> {
    return this.$services.solCustomer.orders?.getCancellationReasonsService(countryCode);
  },

  async cancelPickupService(
    context: Context,
    params: {
      countryCode: string;
      serviceId: string;
      body: ISolCustomerOrderCancellationRequestBody;
    }
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.patchCancelPickupService(
      params.countryCode,
      params.serviceId,
      params.body
    );
  },

  async cancelDeliveryService(
    context: Context,
    params: {
      countryCode: string;
      serviceId: string;
      body: ISolCustomerOrderCancellationRequestBody;
    }
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.patchCancelDeliveryService(
      params.countryCode,
      params.serviceId,
      params.body
    );
  },

  async cancelDeliveryExchangeService(
    context: Context,
    params: {
      countryCode: string;
      serviceId: string;
      body: ISolCustomerOrderCancellationRequestBody;
    }
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.patchCancelDeliveryExchangeService(
      params.countryCode,
      params.serviceId,
      params.body
    );
  },

  async cancelSupplyMaterialService(
    context: Context,
    params: {
      countryCode: string;
      serviceId: string;
      body: ISolCustomerOrderCancellationRequestBody;
    }
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.patchCancelSupplyMaterialService(
      params.countryCode,
      params.serviceId,
      params.body
    );
  },

  async cancelAssistanceService(
    context: Context,
    params: {
      countryCode: string;
      serviceId: string;
      body: ISolCustomerOrderCancellationRequestBody;
    }
  ): Promise<ISolCustomerResponse | undefined> {
    return this.$services.solCustomer.orders?.patchCancelAssistanceService(
      params.countryCode,
      params.serviceId,
      params.body
    );
  },

  async completeLogin(): Promise<void> {
    return this.$services.auth.completeLogin();
  },

  async fetchSignin(): Promise<boolean> {
    return this.$services.auth.getSignedIn();
  },

  async logout(context: Context): Promise<void> {
    context.commit("SET_SIGNED_IN", false);
    await this.$services.auth.logout();
  },

  async generateOrdersReport(context: Context, request?: IRequestClientsReport): Promise<void> {
    await this.$services.solCustomer.orders?.postGenerateOrdersReport(request);
  },

  async fetchTypeClientsReport(): Promise<IUserClientReportTypeData[] | undefined> {
    return this.$services.solCustomer.orders?.getTypeClientsReport();
  },

  async generateEmployeeSearchReport(
    context: Context,
    request?: IEmployeeSearchReportRequest
  ): Promise<void> {
    await this.$services.solCustomer.employees?.postGenerateEmployeeSearchReport(request);
  },

  async generateSubclientsReport(
    context: Context,
    request?: ISubclientsReportRequest
  ): Promise<void> {
    await this.$services.subclient.postGenerateSubclientsReport(request);
  },

  async generateServicePointReport(
    context: Context,
    request?: IServicePointReportRequest
  ): Promise<void> {
    await this.$services.servicepoint.postGenerateServicePointReport(request);
  },

  async generateClientsReport(context: Context, request?: IClientsReportRequest): Promise<void> {
    await this.$services.client.postGenerateClientsReport(request);
  },
};
